import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import { IconButton, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormAddUserSchema } from 'utils/validate';
import { CredentialsOption, FormCreateUser, Role, User, ViewRestrictionsOption } from '../type';
import { InputText } from 'app/components/elements/InputText';
import { ButtonCustom, ButtonOutLine } from 'app/components/elements/ButtonCustom';
import { SelectChangeEvent } from '@mui/material/Select';
import { InputSelect, SelectOptions } from 'app/components/elements/InputSelect';
import { InputSelectMultiple } from 'app/components/elements/InputSelectMultiple';
import { TextError } from 'app/components/elements/TextError';
import { includes } from 'lodash';
import { useAuth } from 'contexts/auth';

type ModalAddUserProps = {
  isEdit?: boolean;
  isOpen: boolean;
  onCloseModal: () => void;
  onSubmit: (value: FormCreateUser) => void;
  optionClient?: SelectOptions[];
  initData?: User;
};
export const ModalAddUser = ({
  isOpen,
  onCloseModal,
  onSubmit,
  optionClient,
  initData,
  isEdit
}: ModalAddUserProps) => {
  const { auth } = useAuth();
  const {
    handleSubmit,
    register,
    control,
    setValue,
    reset,
    watch,
    setError,
    formState: { errors }
  } = useForm<FormCreateUser>({ resolver: yupResolver(FormAddUserSchema) });
  const fileInputIconRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (initData && isOpen) {
      const resetData = {
        name: initData.name,
        email: initData.email,
        phone: initData.phone || '',
        clientIds: initData.accessibleClients?.map((item) => item.id.toString()),
        role: initData.role,
        viewAccess: initData?.viewAccess
          ? initData.viewAccess.map((item) => item.toLowerCase())
          : [],
        editAccess: initData?.editAccess
          ? initData.editAccess.map((item) => item.toLowerCase())
          : [],
        url: initData.photo.url,
        photoId: initData.photo.id
      };
      reset(resetData);
      return;
    }
    if (!isOpen) {
      reset({
        name: '',
        email: '',
        phone: '',
        role: undefined,
        clientIds: undefined,
        viewAccess: undefined,
        editAccess: undefined,
        file: undefined,
        photoId: undefined,
        url: undefined
      });
    }
  }, [initData, isOpen, reset]);
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    setValue('file', fileList[0]);
  };
  const handleFileClick = useCallback(() => {
    if (fileInputIconRef.current) {
      fileInputIconRef.current.click();
    }
  }, []);

  const handleDeleteLogo = useCallback(async () => {
    setValue('file', undefined);
    setValue('url', undefined);
  }, [setValue]);

  const handleSave = (value: FormCreateUser) => {
    if (!includes([Role.SUPER_ADMIN, Role.ADMIN], value.role)) {
      if (!value.clientIds?.length) {
        setError('clientIds', { message: 'Please select at least one client' });
        return;
      }
      setError('clientIds', { message: undefined });
    }
    onSubmit(value);
  };
  // const onchangeCredentials = () => {
  //   if (watch('role') === Role.USER) {
  //     setValue(
  //       'viewAccess',
  //       ViewRestrictionsOption.map((item) => item.value)
  //     );
  //     setValue('viewAccess', ViewRestrictionsOption);
  //   } else {
  //     setValue('viewAccess', ['documentation']);
  //   }
  //   if (watch('role') === Role.SUPER_ADMIN || watch('role') === Role.ADMIN) {
  //     setValue('clientIds', []);
  //   }
  // };
  const url = watch('url');
  return (
    <DialogConfirm
      maxWidth={700}
      textTitle={isEdit ? 'Edit User' : 'Add User'}
      isOpen={isOpen}
      onClose={onCloseModal}>
      <Stack width={'100%'} alignItems={'flex-end'}>
        <Stack width={'100%'} flexDirection={'row'} alignItems={'flex-start'} gap={'20px'}>
          <Stack alignItems={'center'} justifyContent={'flex-start'}>
            <Typography fontSize={'16px'} mb={1} fontWeight={700} color={'#818181'}>
              Profile Picture
            </Typography>
            <input
              accept="image/*"
              ref={fileInputIconRef}
              hidden
              onChange={(event) => handleFileChange(event)}
              type="file"
            />
            {isEdit ? (
              <>
                {!watch('file') && url && (
                  <img
                    width={170}
                    height={170}
                    style={{ objectFit: 'contain' }}
                    src={url}
                    alt={'icon'}
                  />
                )}
                {watch('file') && (
                  <img
                    width={170}
                    height={170}
                    style={{ objectFit: 'contain' }}
                    src={URL.createObjectURL(watch('file'))}
                    alt={'icon'}
                  />
                )}
                {(url && !watch('file')) || watch('file') ? (
                  <img
                    alt={'icon-user'}
                    src={'/icons/icon_delete.svg'}
                    width={24}
                    onClick={handleDeleteLogo}
                  />
                ) : (
                  <Stack
                    width={170}
                    height={170}
                    style={{ backgroundColor: '#D9D9D9' }}
                    alignItems={'center'}
                    justifyContent={'center'}>
                    <IconButton onClick={handleFileClick}>
                      <img
                        width={53}
                        height={53}
                        alt={'icon-user'}
                        src={'/icons/icon_logo_upload.svg'}
                      />
                    </IconButton>
                  </Stack>
                )}
              </>
            ) : (
              <>
                {watch('file') && (
                  <img
                    width={170}
                    height={170}
                    style={{ objectFit: 'contain' }}
                    src={URL.createObjectURL(watch('file'))}
                    alt={'icon'}
                  />
                )}
                {watch('file') ? (
                  <img
                    alt={'icon-user'}
                    src={'/icons/icon_delete.svg'}
                    width={24}
                    onClick={handleDeleteLogo}
                  />
                ) : (
                  <Stack
                    width={170}
                    height={170}
                    style={{ backgroundColor: '#D9D9D9' }}
                    alignItems={'center'}
                    justifyContent={'center'}>
                    <IconButton onClick={handleFileClick}>
                      <img
                        width={53}
                        height={53}
                        alt={'icon-user'}
                        src={'/icons/icon_logo_upload.svg'}
                      />
                    </IconButton>
                  </Stack>
                )}
              </>
            )}

            {errors.file?.message && <TextError errorText={errors.file.message as string} />}
          </Stack>
          <Stack style={{ width: '70%', gap: '20px' }}>
            <InputText
              inputError={errors.name?.message}
              placeholder={'Enter name'}
              title={'Name'}
              inputProps={{
                ...register('name')
              }}
            />
            <InputText
              inputError={errors.email?.message}
              placeholder={'Enter email'}
              title={'Email'}
              inputProps={{
                ...register('email'),
                disabled: isEdit
              }}
            />
            <InputText
              inputError={errors.phone?.message}
              placeholder={'+15551234567'}
              title={'Mobile number'}
              inputProps={{
                ...register('phone')
              }}
            />
            {optionClient && (
              <Controller
                render={({ field }) => (
                  <InputSelectMultiple
                    inputError={errors.clientIds?.message as string}
                    placeholder={'Select clients'}
                    label={'Allowed Client Access'}
                    options={optionClient}
                    disabled={includes([Role.SUPER_ADMIN, Role.ADMIN], watch('role'))}
                    listChooseSelect={watch('clientIds') || []}
                    inputProps={{
                      ...field,
                      defaultValue: [],
                      style: { width: '100%' }
                    }}
                  />
                )}
                name="clientIds"
                control={control}
              />
            )}
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputSelect
                  inputError={errors.role?.message as string}
                  title={'Credentials'}
                  value={value}
                  placeholder={'Select Credentials'}
                  onChange={(e: SelectChangeEvent<unknown>) => {
                    onChange(e);
                    // onchangeCredentials();
                  }}
                  sx={{ padding: 0 }}
                  options={
                    auth?.role === Role.SUPER_ADMIN
                      ? CredentialsOption
                      : CredentialsOption.filter((item) => item.value !== Role.SUPER_ADMIN)
                  }
                />
              )}
              name={'role'}
            />
            <Controller
              render={({ field }) => (
                <InputSelectMultiple
                  listChooseSelect={watch('viewAccess') || []}
                  disabled={includes([Role.SUPER_ADMIN, Role.ADMIN], watch('role'))}
                  inputError={errors.viewAccess?.message as string}
                  placeholder={'Select client permissions'}
                  label={'View Access'}
                  options={
                    [Role.SUPER_ADMIN, Role.ADMIN].includes(auth?.role || '')
                      ? ViewRestrictionsOption
                      : ViewRestrictionsOption.filter((item) => item.value === 'documentation')
                  }
                  inputProps={{
                    ...field,
                    defaultValue: [],
                    style: { width: '100%' }
                  }}
                />
              )}
              name="viewAccess"
              control={control}
            />
            <Controller
              render={({ field }) => (
                <InputSelectMultiple
                  listChooseSelect={watch('editAccess') || []}
                  disabled={includes([Role.SUPER_ADMIN, Role.ADMIN], watch('role'))}
                  inputError={errors.editAccess?.message as string}
                  placeholder={'Select client permissions'}
                  label={'Edit Access'}
                  options={
                    [Role.SUPER_ADMIN, Role.ADMIN].includes(auth?.role || '')
                      ? ViewRestrictionsOption
                      : ViewRestrictionsOption.filter((item) => item.value === 'documentation')
                  }
                  inputProps={{
                    ...field,
                    defaultValue: [],
                    style: { width: '100%' }
                  }}
                />
              )}
              name="editAccess"
              control={control}
            />
          </Stack>
        </Stack>
        <Stack mt={'30px'} flexDirection={'row'} gap={'20px'}>
          <ButtonOutLine
            label={'Cancel'}
            style={{ width: '166px' }}
            onClick={onCloseModal}
            fullWidth
          />
          <ButtonCustom
            label={isEdit ? 'Save' : 'Add'}
            style={{ width: '166px' }}
            onClick={handleSubmit(handleSave)}
            fullWidth
          />
        </Stack>
      </Stack>
    </DialogConfirm>
  );
};
